/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useState, Fragment, memo, useMemo, SyntheticEvent, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import Style from './../../css/LoanHistory.module.css';
import { NumericFormat, numericFormatter, PatternFormat } from 'react-number-format';
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  FormControl,
  TextField,
  Button,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  getTimestampAsDate,
  isStrEmptyOrNull,
  isTransactionProcessing,
  isValidRoutingNumber,
} from '../../common/UtilityFunctions';
import { useOneTimePaymentContext } from '../../contexts/OneTimePaymentContext';
import EnvelopeWithBox from '../EnvelopeWithBox';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Styles from '../../css/OneTimePaymentTable.module.css';
import PageStyles from '../../css/OneTimePaymentPage.module.css';
import EnvelopeStyles from '../../css/EnvelopeWithBox.module.css';
import useGetLoanBalances from '../../common/hooks/useGetLoanBalances';
import { useLoanContext } from '../../contexts/LoanContext';
import { useViewingContext } from '../../contexts/ViewingContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { UPCOMING_PAYMENT_TRANSACTION_CODES } from '../../contracts/constants';

type Payment = {
  dateReceived: number;
  timestamp: number;
  mcdTransType: string;
  mcdTransSuffix: string;
  amount: number;
  prinCurtailReqstd: number;
  extraEscrowReqstd: number;
  lateChargeReqstd: number;
  corpAdvanceReqstd: number;
  assessmentsReqstd: number;
  suspenseReqstd: number;
  batchNo: string;
  cnt: string;
};

const OneTimePaymentTable = memo(() => {
  const { loanNumber } = useLoanContext();
  const oneTimePaymentContext = useOneTimePaymentContext();
  const { loanBalancesInfo, loanBalancesStatus } = useGetLoanBalances(loanNumber, dayjs().format('YYYYMMDD'));
  const { isServicerView } = useViewingContext();
  const { fullAccess } = useAuthContext();
  const [formData, setFormData] = useState({
    dateReceived: dayjs(),
    timestamp: 0,
    mcdTransType: '',
    mcdTransSuffix: '',
    amount: 0,
    prinCurtailReqstd: 0,
    extraEscrowReqstd: 0,
    lateChargeReqstd: 0,
    corpAdvanceReqstd: 0,
    assessmentsReqstd: 0,
    suspenseReqstd: 0,
    routingNo: '',
    accountNo: '',
    accountTypeKeyword: '',
    batchNo: '',
    cnt: '',
  });
  const [monthlyPaymentAmount, setMonthlyPaymentAmount] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [saveTrigger, setSaveTrigger] = useState(false);

  const [additionalPrincipalError, setAdditionalPrincipalError] = useState(false);
  const [additionalEscrowError, setAdditionalEscrowError] = useState(false);
  const [lateChargesError, setLateChargesError] = useState(false);
  const [lateChargesMaxError, setLateChargesMaxError] = useState(false);
  const [corporateAdvanceError, setCorporateAdvanceError] = useState(false);
  const [corporateAdvanceMaxError, setCorporateAdvanceMaxError] = useState(false);
  const [assessmentsError, setAssessmentsError] = useState(false);
  const [assessmentsMaxError, setAssessmentsMaxError] = useState(false);
  const [suspenseError, setSuspenseError] = useState(false);
  const [suspenseMaxError, setSuspenseMaxError] = useState(false);
  const [totalError, setTotalError] = useState(false);
  const [amountError, setAmountError] = useState(false);
  const [routingNumberError, setRoutingNumberError] = useState(false);
  const [bankNameError, setBankNameError] = useState(false);
  const [bankAccountNoError, setBankAccountNoError] = useState(false);
  const [bankAccountTypeError, setBankAccountTypeError] = useState(false);

  const [additionalPrincipalErrorMsg, setAdditionalPrincipalErrorMsg] = useState('');
  const [additionalEscrowErrorMsg, setAdditionalEscrowErrorMsg] = useState('');
  const [lateChargesErrorMsg, setLateChargesErrorMsg] = useState('');
  const [corporateAdvanceErrorMsg, setCorporateAdvanceErrorMsg] = useState('');
  const [assessmentsErrorMsg, setAssessmentsErrorMsg] = useState('');
  const [suspenseErrorMsg, setSuspenseErrorMsg] = useState('');
  const [totalErrorMsg, setTotalErrorMsg] = useState('');
  const [amountErrorMsg, setAmountErrorMsg] = useState('');
  const [routingNumberErrorMsg, setRoutingNumberErrorMsg] = useState('');
  const [bankNameErrorMsg, setBankNameErrorMsg] = useState('');
  const [bankAccountNoErrorMsg, setBankAccountNoErrorMsg] = useState('');
  const [bankAccountTypeErrorMsg, setBankAccountTypeErrorMsg] = useState('');
  const [processDateError, setProcessDateError] = useState(false);

  // today's date and time
  const now = useMemo(() => new Date(), []);

  // transactions are processed on their process date at 1:45 PM PT and 11:45 PM PT.
  // any transaction being actively processed will not be modifiable
  const processingTransactions = useMemo(() => {
    return oneTimePaymentContext.futurePayments
      .filter((row) => UPCOMING_PAYMENT_TRANSACTION_CODES.has(row.mcdTransType))
      .map((row) => {
        const isProcessing = isTransactionProcessing(row.dateReceived);

        return {
          ...row,
          isProcessing,
        };
      });
  }, [oneTimePaymentContext.futurePayments]);

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({
      dateReceived: dayjs(),
      timestamp: 0,
      mcdTransType: '',
      mcdTransSuffix: '',
      amount: 0,
      prinCurtailReqstd: 0,
      extraEscrowReqstd: 0,
      lateChargeReqstd: 0,
      corpAdvanceReqstd: 0,
      assessmentsReqstd: 0,
      suspenseReqstd: 0,
      routingNo: '',
      accountNo: '',
      accountTypeKeyword: '',
      batchNo: '',
      cnt: '',
    });
    setAdditionalPrincipalError(false);
    setAdditionalEscrowError(false);
    setLateChargesError(false);
    setLateChargesMaxError(false);
    setCorporateAdvanceError(false);
    setCorporateAdvanceMaxError(false);
    setAssessmentsError(false);
    setAssessmentsMaxError(false);
    setSuspenseError(false);
    setSuspenseMaxError(false);
    setTotalError(false);
    setAmountError(false);
    setRoutingNumberError(false);
    setBankAccountNoError(false);
    setBankAccountTypeError(false);
    setAdditionalPrincipalErrorMsg('');
    setAdditionalEscrowErrorMsg('');
    setLateChargesErrorMsg('');
    setCorporateAdvanceErrorMsg('');
    setAssessmentsErrorMsg('');
    setSuspenseErrorMsg('');
    setTotalErrorMsg('');
    setAmountErrorMsg('');
    setRoutingNumberErrorMsg('');
    setBankAccountNoErrorMsg('');
    setBankAccountTypeErrorMsg('');
  };

  // set the form data to the given record and editingId to the given index
  const handleEditRow = (index, record) => {
    // if this row is already being edited, close the edit dropdown + reset form
    if (index === editingId) {
      setEditingId(null);
      setFormData({
        dateReceived: dayjs(),
        timestamp: 0,
        mcdTransType: '',
        mcdTransSuffix: '',
        amount: 0,
        prinCurtailReqstd: 0,
        extraEscrowReqstd: 0,
        lateChargeReqstd: 0,
        corpAdvanceReqstd: 0,
        assessmentsReqstd: 0,
        suspenseReqstd: 0,
        routingNo: '',
        accountNo: '',
        accountTypeKeyword: '',
        batchNo: '',
        cnt: '',
      });
      setAdditionalPrincipalError(false);
      setAdditionalEscrowError(false);
      setLateChargesError(false);
      setLateChargesMaxError(false);
      setCorporateAdvanceError(false);
      setCorporateAdvanceMaxError(false);
      setAssessmentsError(false);
      setAssessmentsMaxError(false);
      setSuspenseError(false);
      setSuspenseMaxError(false);
      setTotalError(false);
      setAmountError(false);
      setRoutingNumberError(false);
      setBankAccountNoError(false);
      setBankAccountTypeError(false);
      setAdditionalPrincipalErrorMsg('');
      setAdditionalEscrowErrorMsg('');
      setLateChargesErrorMsg('');
      setCorporateAdvanceErrorMsg('');
      setAssessmentsErrorMsg('');
      setSuspenseErrorMsg('');
      setTotalErrorMsg('');
      setAmountErrorMsg('');
      setRoutingNumberErrorMsg('');
      setBankAccountNoErrorMsg('');
      setBankAccountTypeErrorMsg('');
    } else {
      setEditingId(index);
      setFormData({
        ...record,
        dateReceived: dayjs(record.dateReceived * 1000),
      });
      if (record.mcdTransType === '05' || record.mcdTransType === '08') {
        const totalWithoutMonthlyPayment =
          record.prinCurtailReqstd +
          record.extraEscrowReqstd +
          record.lateChargeReqstd +
          record.corpAdvanceReqstd +
          record.assessmentsReqstd +
          record.suspenseReqstd;
        setMonthlyPaymentAmount(record.amount - totalWithoutMonthlyPayment);
      }
    }
  };

  // delete the given row
  const handleDelete = (e: SyntheticEvent, row) => {
    e.preventDefault();
    oneTimePaymentContext.setDeleteRow(row);
    oneTimePaymentContext.setOneTimePaymentModalContent('delete');
    oneTimePaymentContext.setOneTimePaymentModalOpen(true);
  };

  // update a field in the form
  const handleChange = (name, value) => {
    const amountFields = [
      'prinCurtailReqstd',
      'extraEscrowReqstd',
      'lateChargeReqstd',
      'corpAdvanceReqstd',
      'assessmentsReqstd',
      'suspenseReqstd',
      'amount',
    ];
    if (amountFields.includes(name)) {
      const cleanValue = value.replace(/[$,]/g, '');
      const newValue = parseFloat(cleanValue);
      const oldValue = formData[name];
      const newTotal = formData.amount - oldValue + newValue;

      if (name === 'amount') {
        switch (formData.mcdTransType) {
          case '11':
            setFormData((prev) => ({ ...prev, amount: newValue, prinCurtailReqstd: newValue }));
            break;
          case '13':
            setFormData((prev) => ({ ...prev, amount: newValue, extraEscrowReqstd: newValue }));
            break;
          case '14':
            setFormData((prev) => ({ ...prev, amount: newValue, lateChargeReqstd: newValue }));
            break;
          case '26':
            setFormData((prev) => ({ ...prev, amount: newValue, corpAdvanceReqstd: newValue }));
            break;
          case '20':
            setFormData((prev) => ({ ...prev, amount: newValue, assessmentsReqstd: newValue }));
            break;
          case '16':
            setFormData((prev) => ({ ...prev, amount: newValue, suspenseReqstd: newValue }));
            break;
          default:
            setFormData((prev) => ({ ...prev, [name]: newValue }));
        }
      } else {
        setFormData((prev) => ({ ...prev, [name]: newValue, amount: newTotal }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // validate fields and set relevant errors
  const validateFields = useCallback(
    async (e: SyntheticEvent) => {
      e.preventDefault();
      let isError = false;

      // process date errors
      const processDate = dayjs(formData.dateReceived);
      if (!processDate.isValid() || processDate.isBefore(dayjs(), 'day')) {
        setProcessDateError(true);
        isError = true;
      } else {
        setProcessDateError(false);
      }

      // additional principal errors
      if (isNaN(formData.prinCurtailReqstd)) {
        setAdditionalPrincipalError(true);
        setAdditionalPrincipalErrorMsg('Additional Principal Required');
        isError = true;
      } else {
        setAdditionalPrincipalError(false);
        setAdditionalPrincipalErrorMsg('');
      }

      // late charge errors
      if (isNaN(formData.lateChargeReqstd)) {
        setLateChargesError(true);
        setLateChargesErrorMsg('Late Charges Required');
        isError = true;
      } else if (formData.lateChargeReqstd > loanBalancesInfo.lcAcrdBal) {
        setLateChargesMaxError(true);
        setLateChargesErrorMsg(
          `Your maximum late charges balance is ${numericFormatter(loanBalancesInfo.lcAcrdBal.toString(), {
            thousandSeparator: true,
            prefix: '$',
            decimalScale: 2,
            fixedDecimalScale: true,
          })}`,
        );
        isError = true;
      } else {
        setLateChargesError(false);
        setLateChargesMaxError(false);
        setLateChargesErrorMsg('');
      }

      // additional escrow errors
      if (isNaN(formData.extraEscrowReqstd)) {
        setAdditionalEscrowError(true);
        setAdditionalEscrowErrorMsg('Additional Escrow Required');
        isError = true;
      } else {
        setAdditionalEscrowError(false);
        setAdditionalEscrowErrorMsg('');
      }

      // corporate advance errors
      if (isNaN(formData.corpAdvanceReqstd)) {
        setCorporateAdvanceError(true);
        setCorporateAdvanceErrorMsg('Corporate Advance Required');
        isError = true;
      } else if (formData.corpAdvanceReqstd > -1 * loanBalancesInfo.corpAdvBal) {
        setCorporateAdvanceMaxError(true);
        setCorporateAdvanceErrorMsg(
          `Your maximum corporate advance balance is ${numericFormatter(loanBalancesInfo.corpAdvBal.toString(), {
            thousandSeparator: true,
            prefix: '$',
            decimalScale: 2,
            fixedDecimalScale: true,
          })}`,
        );
        isError = true;
      } else {
        setCorporateAdvanceError(false);
        setCorporateAdvanceMaxError(false);
        setCorporateAdvanceErrorMsg('');
      }

      // assessments errors
      if (isNaN(formData.assessmentsReqstd)) {
        setAssessmentsError(true);
        setAssessmentsErrorMsg('Assessments Required');
        isError = true;
      } else if (formData.assessmentsReqstd > loanBalancesInfo.assessmentBal) {
        setAssessmentsMaxError(true);
        setAssessmentsErrorMsg(
          `Your maximum assessments balance is ${numericFormatter(loanBalancesInfo.assessmentBal.toString(), {
            thousandSeparator: true,
            prefix: '$',
            decimalScale: 2,
            fixedDecimalScale: true,
          })}`,
        );
        isError = true;
      } else {
        setAssessmentsError(false);
        setAssessmentsMaxError(false);
        setAssessmentsErrorMsg('');
      }

      // suspense errors
      if (isNaN(formData.suspenseReqstd)) {
        setSuspenseError(true);
        setSuspenseErrorMsg('Suspense Required');
        isError = true;
      } else if (formData.suspenseReqstd * -1 > loanBalancesInfo.suspenseBal) {
        setSuspenseMaxError(true);
        setSuspenseErrorMsg(
          `Your maximum suspense balance is ${numericFormatter(loanBalancesInfo.suspenseBal.toString(), {
            thousandSeparator: true,
            prefix: '$',
            decimalScale: 2,
            fixedDecimalScale: true,
          })}`,
        );
        isError = true;
      } else {
        setSuspenseError(false);
        setSuspenseMaxError(false);
        setSuspenseErrorMsg('');
      }

      // total errors
      if (formData.amount < 0 || (formData.suspenseReqstd >= 0 && formData.amount === 0)) {
        setTotalError(true);
        setTotalErrorMsg('Invalid Total');
        isError = true;
      } else {
        setTotalError(false);
        setTotalErrorMsg('');
      }

      // routing number errors
      if (!isValidRoutingNumber(formData.routingNo)) {
        setRoutingNumberError(true);
        setRoutingNumberErrorMsg('Invalid Routing Number');
        isError = true;
      } else {
        setRoutingNumberError(false);
        setRoutingNumberErrorMsg('');
      }

      // account number errors
      if (isStrEmptyOrNull(formData.accountNo.replace(/\s/g, ''))) {
        setBankAccountNoError(true);
        setBankAccountNoErrorMsg('Invalid Bank Account Number');
        isError = true;
      } else {
        setBankAccountNoError(false);
        setBankAccountNoErrorMsg('');
      }

      // account type errors
      if (isStrEmptyOrNull(formData.accountTypeKeyword)) {
        setBankAccountTypeError(true);
        setBankAccountTypeErrorMsg('Bank Account Type is Required');
        isError = true;
      } else {
        setBankAccountTypeError(false);
        setBankAccountTypeErrorMsg('');
      }

      if (isError) {
        setAmountError(true);
        return;
      } else {
        setAmountError(false);

        handleSave(e);
      }
    },
    [formData, loanBalancesInfo],
  );

  useEffect(() => {
    switch (formData.mcdTransType) {
      case '11':
        setAmountErrorMsg(additionalPrincipalErrorMsg || '');
        break;
      case '13':
        setAmountErrorMsg(additionalEscrowErrorMsg || '');
        break;
      case '14':
        setAmountErrorMsg(lateChargesErrorMsg || '');
        break;
      case '26':
        setAmountErrorMsg(corporateAdvanceErrorMsg || '');
        break;
      case '20':
        setAmountErrorMsg(assessmentsErrorMsg || '');
        break;
      case '16':
        setAmountErrorMsg(suspenseErrorMsg || '');
        break;
      default:
        setAmountErrorMsg('');
        break;
    }
  }, [
    additionalPrincipalErrorMsg,
    additionalEscrowErrorMsg,
    lateChargesErrorMsg,
    corporateAdvanceErrorMsg,
    assessmentsErrorMsg,
    suspenseErrorMsg,
    formData.mcdTransType,
  ]);

  const handleSave = useCallback(
    (e) => {
      oneTimePaymentContext.handleSaveTransaction(e, { ...formData, dateReceived: formData.dateReceived.unix() });
      setEditingId(null);
      setFormData({
        dateReceived: dayjs(),
        timestamp: 0,
        mcdTransType: '',
        mcdTransSuffix: '',
        amount: 0,
        prinCurtailReqstd: 0,
        extraEscrowReqstd: 0,
        lateChargeReqstd: 0,
        corpAdvanceReqstd: 0,
        assessmentsReqstd: 0,
        suspenseReqstd: 0,
        routingNo: '',
        accountNo: '',
        accountTypeKeyword: '',
        batchNo: '',
        cnt: '',
      });
    },
    [formData],
  );

  const renderEditForm = (row: (typeof processingTransactions)[number]) => (
    <form onSubmit={validateFields}>
      <div className={Styles.editForm}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FormControl fullWidth>
            <DatePicker
              label='Process Date'
              minDate={dayjs()}
              value={formData.dateReceived}
              onChange={(newValue) => {
                handleChange('dateReceived', newValue);

                if (processDateError) {
                  setProcessDateError(false);
                }
              }}
              slotProps={{
                textField: {
                  error: processDateError,
                  helperText: processDateError ? 'Process Date Must Not Be In The Past' : '',
                  size: 'small',
                },
              }}
            />
          </FormControl>
        </LocalizationProvider>
        {row.mcdTransType === '05' || row.mcdTransType === '08' ? (
          <>
            <NumericFormat
              fullWidth
              size='small'
              disabled
              inputProps={{ inputMode: 'numeric' }}
              label='Payment'
              name='payment'
              value={monthlyPaymentAmount}
              customInput={TextField}
              allowNegative={false}
              thousandSeparator={true}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale={true}
            />
            <NumericFormat
              fullWidth
              size='small'
              disabled={!oneTimePaymentContext.isCurrent}
              inputProps={{ inputMode: 'numeric' }}
              label='Additional Principal'
              name='prinCurtailReqstd'
              value={formData.prinCurtailReqstd}
              error={additionalPrincipalError}
              helperText={additionalPrincipalErrorMsg}
              customInput={TextField}
              allowNegative={false}
              thousandSeparator={true}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale={true}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
            <NumericFormat
              fullWidth
              size='small'
              inputProps={{ inputMode: 'numeric' }}
              label='Additional Escrow'
              name='extraEscrowReqstd'
              value={formData.extraEscrowReqstd}
              error={additionalEscrowError}
              helperText={additionalEscrowErrorMsg}
              customInput={TextField}
              allowNegative={false}
              thousandSeparator={true}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale={true}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
            <NumericFormat
              fullWidth
              size='small'
              inputProps={{ inputMode: 'numeric' }}
              label='Late Charges'
              name='lateChargeReqstd'
              value={formData.lateChargeReqstd}
              error={lateChargesError || lateChargesMaxError}
              helperText={lateChargesErrorMsg}
              customInput={TextField}
              allowNegative={false}
              thousandSeparator={true}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale={true}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
            <NumericFormat
              fullWidth
              size='small'
              inputProps={{ inputMode: 'numeric' }}
              label='Corporate Advance'
              name='corpAdvanceReqstd'
              value={formData.corpAdvanceReqstd}
              error={corporateAdvanceError || corporateAdvanceMaxError}
              helperText={corporateAdvanceErrorMsg}
              customInput={TextField}
              allowNegative={false}
              thousandSeparator={true}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale={true}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
            <NumericFormat
              fullWidth
              size='small'
              inputProps={{ inputMode: 'numeric' }}
              label='Assessments'
              name='assessmentsReqstd'
              value={formData.assessmentsReqstd}
              error={assessmentsError || assessmentsMaxError}
              helperText={assessmentsErrorMsg}
              customInput={TextField}
              allowNegative={false}
              thousandSeparator={true}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale={true}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
            <NumericFormat
              fullWidth
              size='small'
              inputProps={{ inputMode: 'numeric' }}
              label='Suspense'
              name='suspenseReqstd'
              value={formData.suspenseReqstd}
              error={suspenseError || suspenseMaxError}
              helperText={suspenseErrorMsg}
              customInput={TextField}
              allowNegative={true}
              thousandSeparator={true}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale={true}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
            <NumericFormat
              fullWidth
              size='small'
              inputProps={{ inputMode: 'numeric' }}
              label='Total'
              name='amount'
              value={formData.amount}
              error={totalError}
              helperText={totalErrorMsg}
              customInput={TextField}
              allowNegative={true}
              thousandSeparator={true}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale={true}
              disabled
            />
          </>
        ) : (
          <NumericFormat
            fullWidth
            size='small'
            inputProps={{ inputMode: 'numeric' }}
            value={formData.amount}
            error={amountError}
            helperText={amountErrorMsg}
            customInput={TextField}
            allowNegative={false}
            thousandSeparator={true}
            prefix={'$'}
            decimalScale={2}
            fixedDecimalScale={true}
            label='Amount'
            name='amount'
            onChange={(e) => handleChange(e.target.name, e.target.value)}
          />
        )}
        <PatternFormat
          fullWidth
          size='small'
          label='Transit Routing No'
          placeholder='Transit Routing No'
          name='routingNo'
          value={formData.routingNo}
          error={routingNumberError}
          helperText={routingNumberErrorMsg}
          customInput={TextField}
          format='#########'
          inputProps={{ inputMode: 'numeric' }}
          onChange={(e) => handleChange(e.target.name, e.target.value)}
        />
        <TextField
          fullWidth
          size='small'
          label='Bank Account No'
          placeholder='Bank Account No *'
          name='accountNo'
          error={bankAccountNoError}
          helperText={bankAccountNoErrorMsg}
          value={formData.accountNo}
          onChange={(e) => handleChange(e.target.name, e.target.value)}
        />
        <FormControl fullWidth>
          <InputLabel id='accountTypeLabel'>Bank Account Type *</InputLabel>
          <Select
            fullWidth
            size='small'
            label='Bank Account Type'
            placeholder='Bank Account Type'
            name='accountTypeKeyword'
            labelId='accountTypeLabel'
            disabled={!fullAccess && isServicerView}
            error={bankAccountTypeError}
            value={formData.accountTypeKeyword}
            onChange={(e) => {
              handleChange(e.target.name, e.target.value);
            }}
          >
            {oneTimePaymentContext.otpBankAcctTypeQidInfo.map((option, key) => (
              <MenuItem key={key} value={option.keyword}>
                {option.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className={Styles.buttonContainer}>
        <Button variant='contained' color='error' onClick={handleCancelEdit}>
          Cancel
        </Button>
        <Button variant='contained' type='submit'>
          Save
        </Button>
      </div>
    </form>
  );

  const renderModifyActions = (row: (typeof processingTransactions)[number], index: number) =>
    row.isProcessing ? (
      <div>This payment is currently being processed and cannot be modified.</div>
    ) : (
      <div className={Styles.buttonColumn}>
        <Button variant='contained' onClick={() => handleEditRow(index, row)}>
          Edit
        </Button>
        <form onSubmit={(e) => handleDelete(e, row)}>
          <Button variant='contained' color='error' type='submit'>
            Delete
          </Button>
        </form>
      </div>
    );

  return (
    <EnvelopeWithBox
      blockName={'Upcoming Payments'}
      className={`${PageStyles.otpEnvelope} ${EnvelopeStyles.tabTitleWrap}`}
    >
      {processingTransactions.length === 0 ? (
        <div>
          No scheduled payments. When payments are scheduled to be processed in the future, they will appear here.
        </div>
      ) : (
        <>
          <div className={Styles.desktopTable}>
            <Table stickyHeader aria-label='sticky table' className={Styles.table}>
              <TableHead>
                <TableRow>
                  <TableCell align='center' className={Style.headerCell}>
                    Confirmation No
                  </TableCell>
                  <TableCell align='center' className={Style.headerCell}>
                    Receive Date
                  </TableCell>
                  <TableCell align='center' className={Style.headerCell}>
                    Process Date
                  </TableCell>
                  <TableCell align='center' className={Style.headerCell}>
                    Transaction Type
                  </TableCell>
                  <TableCell align='center' className={Style.headerCell}>
                    Total
                  </TableCell>
                  <TableCell align='center' className={Style.headerCell}>
                    Source
                  </TableCell>
                  <TableCell align='center' className={Style.headerCell}>
                    Modify
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {processingTransactions.map((row, index) => (
                  <Fragment key={index}>
                    <TableRow className={`${index % 2 === 0 ? Style.even : Style.odd}`}>
                      <TableCell align='center' className={Style.cell}>
                        {row.batchNo + '-' + row.cnt}
                      </TableCell>
                      <TableCell align='center' className={Style.cell}>
                        {getTimestampAsDate(row.timestamp)}
                      </TableCell>
                      <TableCell align='center' className={Style.cell}>
                        {getTimestampAsDate(row.dateReceived)}
                      </TableCell>
                      <TableCell align='center' className={Style.cell}>
                        {row.mcdTransTypeDescription}
                      </TableCell>
                      <TableCell align='center' className={Style.cell}>
                        ${row.amount}
                      </TableCell>
                      <TableCell align='center' className={Style.cell}>
                        {row.mcdTransSuffix === 'H' ? 'Borrower' : 'Cus Rep'}
                      </TableCell>
                      <TableCell align='center' className={Style.cell}>
                        {renderModifyActions(row, index)}
                      </TableCell>
                    </TableRow>

                    {editingId === index && (
                      <TableRow key={'edit' + index}>
                        <TableCell colSpan={7}>{renderEditForm(row)}</TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className={Styles.mobileCards}>
            {processingTransactions.map((row, index) => (
              <Fragment key={index}>
                <div className={`${Styles.paymentCard} ${index % 2 === 0 ? Styles.cardEven : Styles.cardOdd}`}>
                  <div className={Styles.cardRow}>
                    <span className={Styles.cardLabel}>Confirmation No</span>
                    <span className={`${Styles.cardValue} ${Styles.confirmationValue}`}>
                      {row.batchNo}-{row.cnt}
                    </span>
                  </div>
                  <div className={Styles.cardRow}>
                    <span className={Styles.cardLabel}>Receive Date</span>
                    <span className={Styles.cardValue}>{getTimestampAsDate(row.timestamp)}</span>
                  </div>
                  <div className={Styles.cardRow}>
                    <span className={Styles.cardLabel}>Process Date</span>
                    <span className={Styles.cardValue}>{getTimestampAsDate(row.dateReceived)}</span>
                  </div>
                  <div className={Styles.cardRow}>
                    <span className={Styles.cardLabel}>Transaction Type</span>
                    <span className={Styles.cardValue}>{row.mcdTransTypeDescription}</span>
                  </div>
                  <div className={Styles.cardRow}>
                    <span className={Styles.cardLabel}>Total</span>
                    <span className={Styles.cardValue}>${row.amount}</span>
                  </div>
                  <div className={Styles.cardRow}>
                    <span className={Styles.cardLabel}>Source</span>
                    <span className={Styles.cardValue}>{row.mcdTransSuffix === 'H' ? 'Borrower' : 'Cus Rep'}</span>
                  </div>
                  <div className={Styles.cardActions}>{renderModifyActions(row, index)}</div>
                </div>

                {editingId === index && <div className={Styles.mobileEditForm}>{renderEditForm(row)}</div>}
              </Fragment>
            ))}
          </div>
        </>
      )}
    </EnvelopeWithBox>
  );
});

export default OneTimePaymentTable;
